import React from 'react';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import ButtonComponent from '../../../components/button/buttonComponent';
import { ReactComponent as Doc } from 'assets/icons/file.svg';
import Spinner from '../../../components/Spinner';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { formatDateForAppointment } from '../../../utilities/functions';
import { MY_APPOINTMENTS_MY_VISIT_SUMMARY } from '../../../utilities/routes';
import { Card } from './Card';
import { usePreviousAppointmentSummaries } from '../../../hooks/api/useGetPastAppointment';
import { twMerge } from 'tailwind-merge';

interface PreviousAppointmentSummaryProps {
  patientCodexId: string;
}

export const PreviousAppointmentSummary: React.FC<
  PreviousAppointmentSummaryProps
> = ({ patientCodexId }) => {
  const { appointments, loading, hasMore, filter, loadMore } =
    usePreviousAppointmentSummaries(patientCodexId);

  const handleClickViewSummary = (id: string) => {
    window.open(`${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${id}`, '_blank');
  };

  const loaderRef = useInfiniteScroll(() => {
    if (!loading && hasMore) loadMore();
  });

  if (loading && !+filter.offset) return null;

  return (
    <Card
      title={'Previous appointment summary'}
      titleClassName="flex text-h4 text-btn-black font-semibold"
      showSeparator
      defaultExpanded={false}
      Icon={() => <CalendarIcon className="w-6 h-6 fill-btn-black" />}
    >
      <div
        className={twMerge(
          'flex flex-col gap-[10px] h-[250px] overflow-y-auto w-full pb-1 bg-base-content px-5 py-2.5',
          appointments.length && 'border border-gray-opacity-15 rounded-lg',
          !appointments.length && 'bg-white',
        )}
        data-testid="previous-appointments"
      >
        {!appointments.length && (
          <p className="flex items-center m-auto text-med-gray-3 font-medium">
            No previous appointment summaries found
          </p>
        )}
        {appointments.map((appointment) => {
          return (
            <div
              className="flex flex-col bg-white desktop:flex-row justify-between border border-gray-opacity-15 rounded-lg desktop:items-center w-full gap-2.5 py-2.5 px-5 desktop:px-5"
              key={appointment.id}
            >
              <div className="flex flex-col desktop:flex-row desktop:items-center gap-2.5 text-btn-black font-semibold">
                <div className="flex items-center gap-2.5">
                  <div className="text-med-gray-3">
                    <CalendarIcon className={'w-6 h-6 fill-btn-black'} />
                  </div>
                  <div>Appointment date:</div>
                </div>

                <div>
                  {formatDateForAppointment(new Date(appointment.date), {
                    simplified: true,
                    withTimeZone: false,
                  })}
                </div>
              </div>

              <ButtonComponent
                testID="view-summary-button"
                onClick={() => handleClickViewSummary(appointment.id)}
                Icon={Doc}
                type="underline"
                iconPosition="left"
              >
                Summary
              </ButtonComponent>
            </div>
          );
        })}

        {hasMore && (
          <div ref={loaderRef} className="py-2.5 flex justify-center mb-30">
            <Spinner />
          </div>
        )}
      </div>
    </Card>
  );
};
